import { Link as RouterLink } from 'react-router';
import { Button, Stack, Typography } from '@mui/material';
import { SlideshowOutlined } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import { agentPoolsTabPath } from '@/components/agent-pools-admin/agent-pools-tabs';
import OpsDomainPanel from './ops-domain-panel';
import OpsDemandTimeline from './ops-demand-timeline';
import { formatPeakAt } from './ops-format';
import type { OpsMediaAgentExhaustionDto, ProcessorOpsHealthStatus } from '@/types/dtos/ops/ops-platform.dto';

type MediaAgentDomainPanelProps = {
  exhaustion: OpsMediaAgentExhaustionDto;
  health: ProcessorOpsHealthStatus | null;
  demandTimelineFallback?: Array<{
    at: string;
    required: number;
    covered?: number;
    shortfall?: number;
    spareCapacity?: number;
  }>;
};

export default function MediaAgentDomainPanel({
  exhaustion,
  health,
  demandTimelineFallback,
}: MediaAgentDomainPanelProps) {
  const t = useTranslations();

  const demandTimeline = exhaustion.demandTimeline48h?.length
    ? exhaustion.demandTimeline48h
    : (demandTimelineFallback ?? []);

  const headline = exhaustion.poolEmptyWithUpcomingBookings
    ? t(
        'admin.opsAdmin.mediaAgentHeadline.poolEmptyWithUpcomingBookings',
        { pendingBookings: exhaustion.pendingBookings },
        `No free — ${exhaustion.pendingBookings} waiting for assignment`,
      )
    : exhaustion.bookingGaps
      ? t(
          'admin.opsAdmin.mediaAgentHeadline.bookingGaps',
          { count: exhaustion.failedBookings },
          `${exhaustion.failedBookings} failed booking(s)`,
        )
      : exhaustion.peakDemandExceedsCapacity
        ? t(
            'admin.opsAdmin.peakDemandHeadlineConnected',
            {
              peak: exhaustion.peakParallelRequired48h,
              connected: exhaustion.connected,
              shortfall: Math.max(0, exhaustion.peakParallelRequired48h - exhaustion.connected),
            },
            `Peak ${exhaustion.peakParallelRequired48h} parallel — ${Math.max(0, exhaustion.peakParallelRequired48h - exhaustion.connected)} short of ${exhaustion.connected} connected`,
          )
        : exhaustion.belowBaseline
          ? t(
              'admin.opsAdmin.mediaAgentHeadline.belowBaseline',
              { free: exhaustion.free },
              `Below baseline (${exhaustion.free} free)`,
            )
          : undefined;

  return (
    <OpsDomainPanel
      title={t('admin.opsAdmin.mediaAgentMetricsTitle', undefined, 'Media agent pool')}
      icon={<SlideshowOutlined fontSize="small" color="action" />}
      health={health}
      headline={headline}
      footer={
        <Button
          size="small"
          component={RouterLink}
          to={agentPoolsTabPath('media-agent')}
          sx={{ fontSize: '0.7rem', py: 0 }}
        >
          {t('admin.opsAdmin.openMediaAgentPool', undefined, 'Open pool')}
        </Button>
      }
    >
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
        <Typography variant="caption" color="text.secondary">
          {t('admin.opsAdmin.metrics.peakParallel48h', undefined, 'Peak parallel (48h)')}
        </Typography>
        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
          {exhaustion.peakParallelRequired48h}
          {exhaustion.peakAt && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5, fontSize: '0.65rem' }}>
              {t(
                'admin.opsAdmin.peakAt',
                { when: formatPeakAt(exhaustion.peakAt) },
                `@ ${formatPeakAt(exhaustion.peakAt)}`,
              )}
            </Typography>
          )}
        </Typography>
      </Stack>
      <OpsDemandTimeline
        buckets={demandTimeline}
        connectedCapacity={exhaustion.connected}
        peakAt={exhaustion.peakAt}
        peakParallelRequired48h={exhaustion.peakParallelRequired48h}
      />
      <Stack direction="row" spacing={1.5} flexWrap="wrap">
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>
          {exhaustion.connected}/{exhaustion.total} conn
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>
          {exhaustion.free} free
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>
          {exhaustion.upcoming48h} bookings (48h)
        </Typography>
        {exhaustion.failedBookings > 0 && (
          <Typography variant="caption" color="error.main" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>
            {exhaustion.failedBookings} failed
          </Typography>
        )}
      </Stack>
    </OpsDomainPanel>
  );
}
